import React, { useState } from "react";
import { Card, CardBody, Row, Col } from "reactstrap";
import constants from "constants.js";
import ViewUploadedID from "modals/ViewUploadedID";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";

const getPackageImageUrl = (image) =>
  `${constants.s3Bucket}/package-images/${image}`;

const getItemImageUrl = (image) =>
  `${constants.s3Bucket}/item-images/${image}`;

const parseSaved = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { return JSON.parse(val); } catch { return []; }
};

const getItemImages = (item) => {
  if (Array.isArray(item?.images)) return item.images;
  if (typeof item?.images === "string") {
    try {
      const parsed = JSON.parse(item.images);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return item.images.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  if (Array.isArray(item?.image)) return item.image;
  if (typeof item?.image === "string" && item.image) return [item.image];
  return [];
};

const PackageDetails = ({
  packageData = {},
  pickupData = {},
  changedPaths = [],
}) => {
  const { t } = useTranslation();
  const [idUrl, setIdUrl] = useState(null);
  const [viewUploadedIDOpen, setViewUploadedIDOpen] = useState(false);

  const closeUploadedID = () => setViewUploadedIDOpen(false);

  const zoomImage = (url) => {
    setIdUrl(url);
    setViewUploadedIDOpen(true);
  };

  const items = parseSaved(packageData.items);

  return (
    <>
      <Row className="mt-4">
        <Col md="6">
          <Card className="card-profile shadow border-0 mb-3">
            <CardBody className="py-2 px-3">
              <h4 className="heading-md text-muted mb-2">
                {t("modal.packageDetails.title", "Package Details")}
              </h4>

              <p className="description mb-1">
                <strong>{t("modal.packageDetails.weightLabel", "Weight")}:</strong>{" "}
                <span className={helpers.getHighlightClass("packageDetails.weight", changedPaths)}>
                  {packageData.weight || "—"}
                </span>
              </p>

              <p className="description mb-1">
                <strong>{t("page.orders.baggageDetails.size", "Size")}:</strong>{" "}
                <span className={helpers.getHighlightClass("packageDetails.size", changedPaths)}>
                  {packageData.size || "—"}
                </span>
              </p>

              {items.length > 0 ? (
                <div className="mt-2 mb-1">
                  <p className="description mb-1">
                    <strong>{t("modal.packageDetails.descriptionLabel", "Items")}:</strong>
                  </p>
                  <div
                    className={`d-flex flex-column ${helpers.getHighlightClass(["packageDetails.itemsDetails", "packageDetails.items"], changedPaths)}`}
                    style={{ gap: "6px" }}
                  >
                    {items.map((item, i) => {
                      const itemImages = getItemImages(item);
                      return (
                        <div
                          key={i}
                          className="d-flex flex-column"
                          style={{
                            background: "#FAFAFA",
                            border: "1px solid #EEE",
                            borderRadius: "8px",
                            padding: "8px 12px",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <p className="mb-0 heading-sm" style={{ fontWeight: 600, color: "#333" }}>
                                {item.name}
                              </p>
                              {item.category && (
                                <p className="mb-0 text-muted" style={{ fontSize: "0.72rem" }}>
                                  {item.category}
                                </p>
                              )}
                            </div>
                            {item.quantity && (
                              <span
                                style={{
                                  background: "#fff",
                                  border: "1px solid #EEE",
                                  borderRadius: "4px",
                                  fontSize: "0.72rem",
                                  padding: "2px 8px",
                                  color: "#555",
                                  flexShrink: 0,
                                  marginLeft: "8px",
                                }}
                              >
                                {t("page.create-listing.packageDetails.items.quantity", "Qty")}: {item.quantity}
                              </span>
                            )}
                          </div>
                          {itemImages.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {itemImages.map((img, imgIdx) => (
                                <img
                                  key={imgIdx}
                                  src={getItemImageUrl(img)}
                                  alt={item.name || "Item Image"}
                                  className="rounded shadow order-image cursor-pointer mr-2 mb-1"
                                  style={{ width: "80px", height: "80px", objectFit: "cover" }}
                                  onClick={() => zoomImage(getItemImageUrl(img))}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="description mb-1">
                  <strong>{t("modal.packageDetails.detailsLabel", "Item Details")}:</strong>{" "}
                  <span className={helpers.getHighlightClass("packageDetails.itemsDetails", changedPaths)}>
                    {packageData.itemsDetails || "—"}
                  </span>
                </p>
              )}

              {packageData.otherDetails && (
                <p className="description mb-1 mt-2">
                  <strong>{t("modal.packageDetails.otherDetailsLabel", "Other Details")}:</strong>{" "}
                  <span className={helpers.getHighlightClass("packageDetails.otherDetails", changedPaths)}>
                    {packageData.otherDetails}
                  </span>
                </p>
              )}

              {packageData.packageImages?.length > 0 && (
                <div className="mt-3">
                  <p className="mb-1 heading-sm">
                    <strong>{t("page.orders.packageDetails.packageImages", "Package Images")}:</strong>
                  </p>
                  <div className={`d-flex flex-wrap gap-3 ${helpers.getHighlightClass("packageDetails.packageImages", changedPaths)}`}>
                    {packageData.packageImages.map((img, i) => (
                      <img
                        key={i}
                        src={getPackageImageUrl(img)}
                        alt={t("page.orders.packageDetails.packageImages", "Package Images")}
                        className="rounded shadow order-image cursor-pointer mr-2 mb-2"
                        onClick={() => zoomImage(getPackageImageUrl(img))}
                      />
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md="6">
          <Card className="card-profile shadow border-0 mb-3">
            <CardBody className="py-2 px-3">
              <h4 className="heading-md text-muted mb-2">
                {t("page.create-listing.baggageDetails.handoverPreference", "Handover Preference")}
              </h4>

              <p className="description mb-1">
                <strong>{t("page.create-listing.baggageDetails.handoverPreference", "Preference")}:</strong>{" "}
                <span className={helpers.getHighlightClass("pickUpDetails.handoverPreference", changedPaths)}>
                  {pickupData.handoverPreference || "—"}
                </span>
              </p>

              {pickupData.meetingPointHint && (
                <p className="description mb-1">
                  <strong>{t("page.create-listing.baggageDetails.meetingPointHint", "Meeting Point")}:</strong>{" "}
                  <span className={helpers.getHighlightClass("pickUpDetails.meetingPointHint", changedPaths)}>
                    {pickupData.meetingPointHint}
                  </span>
                </p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ViewUploadedID
        idUrl={idUrl}
        isOpen={viewUploadedIDOpen}
        toggle={closeUploadedID}
      />
    </>
  );
};

export default PackageDetails;
